// Financials Tab Component
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Pencil, FileText, Loader2, ChevronDown, Search } from 'lucide-react';
import { toast } from 'sonner';
import { crmAPI, hospitalityAPI } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import InvoicePdfPreviewDialog from '../shared/InvoicePdfPreviewDialog';
import { calcGrossBilled } from '../../utils/invoiceCalc';

const FinancialsTab = ({ account, onUpdate, hospitality = [], users = [], entities = [], canEditAccount = true, isFarm = false }) => {
  const { user } = useAuth();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: farmMetrics } = useQuery({
    queryKey: QK.farmfolioMetricsAccount(account?.id),
    queryFn: () => api.get(`/farmfolio/metrics/account/${account.id}`).then(r => r.data),
    enabled: isFarm && !!account?.id,
  });

  const { data: financialsData, isLoading: loadingFinancials } = useQuery({
    queryKey: [QK.accountInvoices(), QK.accountStatements(), QK.accountGrossSales(account?.id), QK.billingEntities(), account?.id],
    queryFn: async () => {
      const [invRes, stmtRes, grossRes, beRes, tenantRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/invoices/statements'),
        api.get(`/reports/account-gross-sales?crm_entity_id=${account.id}`),
        api.get('/billing-entities'),
        user?.tenant_id ? api.get(`/tenants/${user.tenant_id}`) : Promise.resolve({ data: {} }),
      ]);
      const approvedStatuses = ['approved', 'delivered', 'delivered_manually', 'delivered_via_greendoor'];
      const allForAccount = (invRes.data || []).filter(i =>
        i.crm_entity_id === account.id || (account.is_tenant_managed && i.syndicate_id === account.id)
      );
      return {
        allAccountInvoices: allForAccount,
        invoices: allForAccount.filter(i => approvedStatuses.includes(i.invoice_status)),
        statements: (stmtRes.data || []).filter(s => s.crm_entity_id === account.id),
        grossSales: grossRes.data || { ytd: 0, all_time: 0 },
        billingEntities: beRes.data || [],
        tenantName: tenantRes.data?.name || '',
      };
    },
    enabled: !!account?.id,
  });

  const allAccountInvoices = financialsData?.allAccountInvoices ?? [];
  const invoices           = financialsData?.invoices ?? [];
  const statements         = financialsData?.statements ?? [];
  const grossSales         = financialsData?.grossSales ?? { ytd: 0, all_time: 0 };
  const billingEntities    = financialsData?.billingEntities ?? [];
  const tenantName         = financialsData?.tenantName ?? '';

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isEditingCommission, setIsEditingCommission] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewType, setPreviewType] = useState('invoice');
  const [previewInvoiceId, setPreviewInvoiceId] = useState(null);
  const [previewStmtData, setPreviewStmtData] = useState(null);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [statementSearch, setStatementSearch] = useState('');

  const [commissionForm, setCommissionForm] = useState({
    commission_currency: 'USD',
    commission_rate_percent: '',
    minimum_commission_amount: '',
    commission_split: false,
    commission_split_with: '',
    commission_split_with_entity_id: '',
    commission_split_percent_entity: '',
    commission_split_percent_linked: '',
    withdrawals_fee_shipin: '',
    withdrawals_fee_sale: '',
    expense_markup_percent: '',
  });
  
  const [financialNotes, setFinancialNotes] = useState('');
  
  useEffect(() => {
    if (account) {
      setCommissionForm({
        commission_currency: account.commission_currency || 'USD',
        commission_rate_percent: account.commission_rate_percent || '',
        minimum_commission_amount: account.minimum_commission_amount || '',
        commission_split: account.commission_split || false,
        commission_split_with: account.commission_split_with || '',
        commission_split_with_entity_id: account.commission_split_with_entity_id || '',
        commission_split_percent_entity: account.commission_split_percent_entity || '',
        commission_split_percent_linked: account.commission_split_percent_linked || '',
        withdrawals_fee_shipin: account.withdrawals_fee_shipin || '',
        withdrawals_fee_sale: account.withdrawals_fee_sale || '',
        expense_markup_percent: account.expense_markup_percent || '',
      });
      setFinancialNotes(account.other_financial_notes || '');
    }
  }, [account?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
  };

  const openInvoicePreview = (inv) => {
    setPreviewType('invoice');
    setPreviewInvoiceId(inv.id);
    setPreviewStmtData(null);
    setPreviewOpen(true);
  };

  const openStatementPreview = (stmt) => {
    setPreviewType('statement');
    setPreviewInvoiceId(null);
    setPreviewStmtData(stmt);
    setPreviewOpen(true);
  };
  
  const handleSaveCommission = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, commissionForm);
      toast.success('Commission details updated');
      setIsEditingCommission(false);
      setCommissionOpen(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update commission details:', error);
      toast.error('Failed to update commission details');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      await crmAPI.update(account.id, { other_financial_notes: financialNotes });
      toast.success('Financial notes updated');
      setIsEditingNotes(false);
      setNotesOpen(false);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to update notes:', error);
      toast.error('Failed to update notes');
    } finally {
      setSaving(false);
    }
  };
  
  // Calculate commission and net proceeds totals from invoices
  const currentYear = new Date().getFullYear();
  let commissionsYTD = 0, commissionsAllTime = 0;
  let netProceedsYTD = 0, netProceedsAllTime = 0;

  invoices.forEach(inv => {
    const invYear = inv.invoice_date ? new Date(inv.invoice_date).getFullYear() : null;
    const commission = parseFloat(inv.commission_amount || 0);
    if (commission > 0) {
      commissionsAllTime += commission;
      if (invYear === currentYear) commissionsYTD += commission;
    }
    if (inv.add_sale_proceeds) {
      const saleNet = parseFloat(inv.sale_net_proceeds || 0);
      const ownerPct = parseFloat(inv.owner_percent || 100) / 100;
      const proceeds = (saleNet - commission) * ownerPct;
      netProceedsAllTime += proceeds;
      if (invYear === currentYear) netProceedsYTD += proceeds;
    }
  });

  // --- Top Line Summary computation (all-statuses invoice array) ---
  const fmtC = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v ?? 0);
  const sumAcctAmt = (list) => list.reduce((s, i) => s + calcGrossBilled(i), 0);
  const summaryYTDInvs = allAccountInvoices.filter(i => i.invoice_date && new Date(i.invoice_date).getFullYear() === currentYear);
  const acctTotalYTD     = sumAcctAmt(summaryYTDInvs);
  const acctTotalAll     = sumAcctAmt(allAccountInvoices);
  const acctDraftYTD     = sumAcctAmt(summaryYTDInvs.filter(i => i.invoice_status === 'draft'));
  const acctApprovedYTD  = sumAcctAmt(summaryYTDInvs.filter(i => i.invoice_status === 'approved'));
  const acctDeliveredYTD = sumAcctAmt(summaryYTDInvs.filter(i => ['delivered', 'delivered_manually', 'delivered_via_greendoor'].includes(i.invoice_status)));
  const acctPaidYTD      = sumAcctAmt(summaryYTDInvs.filter(i => i.payment_status === 'paid'));
  const acctUnpaidYTD    = sumAcctAmt(summaryYTDInvs.filter(i => i.payment_status === 'unpaid' && ['delivered', 'delivered_manually', 'delivered_via_greendoor'].includes(i.invoice_status)));
  const hasSalesActivity = commissionsAllTime > 0 || grossSales.all_time > 0;

  // Helper to get entity/user name
  const getSelectedEntityName = () => {
    const splitWith = isEditingCommission ? commissionForm.commission_split_with : account.commission_split_with;
    const entityId = isEditingCommission ? commissionForm.commission_split_with_entity_id : account.commission_split_with_entity_id;
    
    if (!splitWith || !entityId) return '';
    
    if (splitWith === 'account_manager') {
      const user = users.find(u => u.id === entityId);
      return user ? user.name : '';
    } else if (splitWith === 'account' || splitWith === 'contact') {
      const entity = entities.find(e => e.id === entityId);
      return entity ? entity.system_name : '';
    }
    return '';
  };

  return (
    <div className="space-y-6">
      {/* Top Line Summary — unified 3-box + 2 collapsibles */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top Line Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* 3 main boxes — always shown */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
              <p className="text-xs text-muted-foreground uppercase">Total Billed Amount</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtC(acctTotalYTD)}</span></div>
                <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtC(acctTotalAll)}</span></div>
              </div>
            </div>
            <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
              <p className="text-xs text-muted-foreground uppercase">YTD by Status</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-xs font-medium">Draft:</span><span className="text-xs">{fmtC(acctDraftYTD)}</span></div>
                <div className="flex justify-between"><span className="text-xs font-medium">Approved:</span><span className="text-xs">{fmtC(acctApprovedYTD)}</span></div>
                <div className="flex justify-between"><span className="text-xs font-medium">Sent Out:</span><span className="text-xs">{fmtC(acctDeliveredYTD)}</span></div>
              </div>
            </div>
            <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
              <p className="text-xs text-muted-foreground uppercase">YTD Payment Status</p>
              <div className="space-y-1">
                <div className="flex justify-between"><span className="text-xs font-medium">Paid:</span><span className="text-xs text-primary">{fmtC(acctPaidYTD)}</span></div>
                <div className="flex justify-between"><span className="text-xs font-medium">Unpaid:</span><span className="text-xs text-red-600">{fmtC(acctUnpaidYTD)}</span></div>
              </div>
            </div>
          </div>

          {/* Sales Summary — shown if this account has sales activity */}
          {hasSalesActivity && (
            <Collapsible defaultOpen>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase hover:text-foreground transition-colors w-full text-left">
                <ChevronDown className="w-3.5 h-3.5" />
                Sales Summary
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">All Commissions</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtC(commissionsYTD)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtC(commissionsAllTime)}</span></div>
                    </div>
                  </div>
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">Gross Sales</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtC(grossSales.ytd)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtC(grossSales.all_time)}</span></div>
                    </div>
                  </div>
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">Owner Net Proceeds</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtC(netProceedsYTD)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtC(netProceedsAllTime)}</span></div>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* FarmFolio Summary — shown for farm tenants */}
          {isFarm && farmMetrics && (
            <Collapsible defaultOpen>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase hover:text-foreground transition-colors w-full text-left">
                <ChevronDown className="w-3.5 h-3.5" />
                FarmFolio Summary
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">Farm Spend</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtC(farmMetrics.farm_spend?.ytd ?? 0)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtC(farmMetrics.farm_spend?.all_time ?? 0)}</span></div>
                    </div>
                  </div>
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">Net Revenue</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">YTD:</span><span className="text-xs">{fmtC(farmMetrics.net_revenue?.ytd ?? 0)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">All Time:</span><span className="text-xs">{fmtC(farmMetrics.net_revenue?.all_time ?? 0)}</span></div>
                    </div>
                  </div>
                  <div className="space-y-1 p-3 rounded" style={{ backgroundColor: 'rgba(52, 107, 78, 0.1)' }}>
                    <p className="text-xs text-muted-foreground uppercase">Unbilled / Outstanding</p>
                    <div className="space-y-1">
                      <div className="flex justify-between"><span className="text-xs font-medium">Unbilled:</span><span className={`text-xs ${(farmMetrics.unbilled_expenses ?? 0) > 0 ? 'text-amber-600 font-semibold' : ''}`}>{fmtC(farmMetrics.unbilled_expenses ?? 0)}</span></div>
                      <div className="flex justify-between"><span className="text-xs font-medium">Outstanding:</span><span className={`text-xs ${(farmMetrics.outstanding_balance ?? 0) > 0 ? 'text-red-600 font-semibold' : ''}`}>{fmtC(farmMetrics.outstanding_balance ?? 0)}</span></div>
                    </div>
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Invoices</CardTitle>
          </div>
          <div className="relative w-56 mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input className="pl-9 h-8 text-sm" placeholder="Search invoices..." value={invoiceSearch} onChange={(e) => setInvoiceSearch(e.target.value)} data-testid="fin-invoice-search" />
          </div>
        </CardHeader>
        <CardContent className="px-6 pb-6 pt-0">
          {loadingFinancials ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (() => {
            const q = invoiceSearch.toLowerCase();
            const filtered = q ? invoices.filter(inv => {
              const beName = inv.billing_entity_id
                ? (billingEntities.find(e => e.id === inv.billing_entity_id)?.name || '')
                : (tenantName || 'primary');
              return (
                (inv.invoice_number_formatted || '').toLowerCase().includes(q) ||
                (inv.horse_name || '').toLowerCase().includes(q) ||
                (inv.invoice_status || '').toLowerCase().includes(q) ||
                (inv.payment_status || '').toLowerCase().includes(q) ||
                (inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '').includes(q) ||
                beName.toLowerCase().includes(q)
              );
            }) : invoices;
            return filtered.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/20" />
              <p className="text-sm text-muted-foreground">{invoiceSearch ? 'No matching invoices' : 'No approved invoices'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {billingEntities.length > 0 && <TableHead>Billing Entity</TableHead>}
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Horse</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(inv => {
                  const entries = inv.entries || [];
                  const expTotal = entries.filter(e => !e.sale_opportunity_id).reduce((s, e) => s + (e.total || e.price * e.units), 0) - (inv.discount_total || 0);
                  const saleProceeds = inv.add_sale_proceeds ? ((inv.sale_net_proceeds || 0) - (inv.commission_amount || 0)) : 0;
                  const total = inv.add_sale_proceeds ? (saleProceeds - expTotal) : expTotal;
                  const amount = ((inv.owner_percent || 100) / 100) * total;
                  const payable = (inv.add_sale_proceeds && saleProceeds > expTotal) || inv.is_commission_split_invoice;
                  const beName = inv.billing_entity_id
                    ? (billingEntities.find(e => e.id === inv.billing_entity_id)?.name || '–')
                    : (tenantName || '–');
                  return (
                    <TableRow key={inv.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openInvoicePreview(inv)} data-testid={`fin-invoice-row-${inv.id}`}>
                      {billingEntities.length > 0 && <TableCell>{beName}</TableCell>}
                      <TableCell className="font-medium">{inv.invoice_number_formatted || `#${inv.invoice_number}`}</TableCell>
                      <TableCell>{inv.horse_name || '-'}</TableCell>
                      <TableCell className="text-sm">{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell><Badge className="bg-blue-100 text-blue-700">{inv.invoice_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
                      <TableCell><Badge className={inv.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{inv.payment_status?.replace(/\b\w/g, c => c.toUpperCase())}</Badge></TableCell>
                      <TableCell className={`text-right font-medium ${payable ? 'text-primary' : ''}`}>
                        {payable ? `(${fmt(amount)})` : fmt(amount)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          );
          })()}
        </CardContent>
      </Card>

      {/* Statements */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Statements</CardTitle>
          </div>
          <div className="relative w-56 mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input className="pl-9 h-8 text-sm" placeholder="Search statements..." value={statementSearch} onChange={(e) => setStatementSearch(e.target.value)} data-testid="fin-statement-search" />
          </div>
        </CardHeader>
        <CardContent className="px-6 pb-6 pt-0">
          {loadingFinancials ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : (() => {
            const q = statementSearch.toLowerCase();
            const filtered = q ? statements.filter(s =>
              String(s.invoice_count).includes(q) ||
              fmt(s.total_expenses).toLowerCase().includes(q) ||
              fmt(s.total_proceeds).toLowerCase().includes(q) ||
              fmt(s.net_balance).toLowerCase().includes(q) ||
              new Date(s.generated_at).toLocaleDateString().includes(q) ||
              (s.horse_names || []).some(h => h.toLowerCase().includes(q))
            ) : statements;
            return filtered.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/20" />
              <p className="text-sm text-muted-foreground">{statementSearch ? 'No matching statements' : 'No statements generated'}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoices</TableHead>
                  <TableHead>Expenses</TableHead>
                  <TableHead>Proceeds</TableHead>
                  <TableHead>Net Balance</TableHead>
                  <TableHead>Generated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(stmt => (
                  <TableRow key={stmt.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openStatementPreview(stmt)} data-testid={`fin-statement-row-${stmt.id}`}>
                    <TableCell><Badge variant="outline">{stmt.invoice_count}</Badge></TableCell>
                    <TableCell>{fmt(stmt.total_expenses)}</TableCell>
                    <TableCell className="text-primary">{fmt(stmt.total_proceeds)}</TableCell>
                    <TableCell className={`font-medium ${stmt.net_balance >= 0 ? 'text-primary' : 'text-red-600'}`}>
                      {stmt.net_balance >= 0 ? fmt(stmt.net_balance) : `-${fmt(Math.abs(stmt.net_balance))}`}
                    </TableCell>
                    <TableCell className="text-sm">{new Date(stmt.generated_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          );
          })()}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-6">
        {/* Commission Details - Collapsible */}
        <Collapsible open={commissionOpen} onOpenChange={setCommissionOpen}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CollapsibleTrigger className="flex items-center gap-2 hover:opacity-80 cursor-pointer">
              <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform [[data-state=closed]_&]:-rotate-90" />
              <CardTitle className="text-base">Commission Details</CardTitle>
            </CollapsibleTrigger>
            {canEditAccount && !isEditingCommission && (
              <Button variant="ghost" size="icon" onClick={() => { setIsEditingCommission(true); setCommissionOpen(true); }}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CollapsibleContent>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm w-48">Commission Currency</Label>
                <Select 
                  value={isEditingCommission ? commissionForm.commission_currency : (account.commission_currency || 'USD')}
                  onValueChange={(val) => setCommissionForm({...commissionForm, commission_currency: val})}
                  disabled={!isEditingCommission}
                >
                  <SelectTrigger className="w-32 disabled:opacity-100">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="AUD">AUD</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm w-48">Commission Rate %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={isEditingCommission ? commissionForm.commission_rate_percent : (account.commission_rate_percent || '')}
                  onChange={(e) => setCommissionForm({...commissionForm, commission_rate_percent: e.target.value})}
                  placeholder="e.g., 5.00"
                  className="flex-1 disabled:opacity-100"
                  disabled={!isEditingCommission}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm w-48">Minimum Commission Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={isEditingCommission ? commissionForm.minimum_commission_amount : (account.minimum_commission_amount || '')}
                  onChange={(e) => setCommissionForm({...commissionForm, minimum_commission_amount: e.target.value})}
                  placeholder="e.g., 1000.00"
                  className="flex-1 disabled:opacity-100"
                  disabled={!isEditingCommission}
                />
              </div>
              
              <div className="flex items-center gap-4">
                <Label className="text-sm">Commission Split</Label>
                <Switch
                  checked={isEditingCommission ? commissionForm.commission_split : account.commission_split}
                  onCheckedChange={(checked) => setCommissionForm({...commissionForm, commission_split: checked})}
                  disabled={!isEditingCommission}
                  className="disabled:opacity-100"
                />
              </div>
              
              {((isEditingCommission && commissionForm.commission_split) || 
                (!isEditingCommission && account.commission_split)) && (
                <>
                  <div className="space-y-2 pl-4 border-l-2 border-muted">
                    <Label className="text-sm">Commission Split with</Label>
                    <div className="space-y-2">
                      {['account_manager', 'account', 'contact', 'other'].map((option) => (
                        <label key={option} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="commission_split_with"
                            checked={(isEditingCommission ? commissionForm.commission_split_with : account.commission_split_with) === option}
                            onChange={() => setCommissionForm({...commissionForm, commission_split_with: option, commission_split_with_entity_id: ''})}
                            className="rounded-full disabled:opacity-100"
                            disabled={!isEditingCommission}
                          />
                          <span className="text-sm capitalize">{option.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                    
                    {/* Show dropdown based on selection */}
                    {((isEditingCommission && commissionForm.commission_split_with) || 
                      (!isEditingCommission && account.commission_split_with)) && (
                      <>
                        {((isEditingCommission ? commissionForm.commission_split_with : account.commission_split_with) === 'account_manager') && (
                          <div className="mt-2">
                            <Label className="text-sm text-xs mb-1">Select Account Manager</Label>
                            <Select 
                              value={isEditingCommission ? commissionForm.commission_split_with_entity_id : account.commission_split_with_entity_id}
                              onValueChange={(val) => setCommissionForm({...commissionForm, commission_split_with_entity_id: val})}
                              disabled={!isEditingCommission}
                            >
                              <SelectTrigger className="disabled:opacity-100">
                                <SelectValue placeholder="Select user" />
                              </SelectTrigger>
                              <SelectContent>
                                {users.map(user => (
                                  <SelectItem key={user.id} value={user.id}>
                                    {user.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {(((isEditingCommission ? commissionForm.commission_split_with : account.commission_split_with) === 'account') || 
                          ((isEditingCommission ? commissionForm.commission_split_with : account.commission_split_with) === 'contact')) && (
                          <div className="mt-2">
                            <Label className="text-sm text-xs mb-1">Select {(isEditingCommission ? commissionForm.commission_split_with : account.commission_split_with) === 'account' ? 'Account' : 'Contact'}</Label>
                            <Select 
                              value={isEditingCommission ? commissionForm.commission_split_with_entity_id : account.commission_split_with_entity_id}
                              onValueChange={(val) => setCommissionForm({...commissionForm, commission_split_with_entity_id: val})}
                              disabled={!isEditingCommission}
                            >
                              <SelectTrigger className="disabled:opacity-100">
                                <SelectValue placeholder="Select entity" />
                              </SelectTrigger>
                              <SelectContent>
                                {entities
                                  .filter(entity => {
                                    const splitType = isEditingCommission ? commissionForm.commission_split_with : account.commission_split_with;
                                    if (splitType === 'account') {
                                      return entity.crm_class === 'account';
                                    } else if (splitType === 'contact') {
                                      return entity.crm_class === 'contact';
                                    }
                                    return false;
                                  })
                                  .map(entity => (
                                    <SelectItem key={entity.id} value={entity.id}>
                                      {entity.system_name}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        {((isEditingCommission ? commissionForm.commission_split_with : account.commission_split_with) === 'other') && (
                          <div className="mt-2">
                            <Label className="text-sm text-xs mb-1">Other Entity Name</Label>
                            <Input
                              value={isEditingCommission ? commissionForm.commission_split_with_entity_id : account.commission_split_with_entity_id}
                              onChange={(e) => setCommissionForm({...commissionForm, commission_split_with_entity_id: e.target.value})}
                              placeholder="Enter entity name"
                              className="disabled:opacity-100"
                              disabled={!isEditingCommission}
                            />
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Commission Split % - Inside the indented section */}
                    <div className="flex items-center gap-2 mt-3">
                      <Label className="text-sm w-40">Commission Split %</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={isEditingCommission ? commissionForm.commission_split_percent_linked : (account.commission_split_percent_linked || '')}
                        onChange={(e) => setCommissionForm({...commissionForm, commission_split_percent_linked: e.target.value})}
                        placeholder="e.g., 50.00"
                        className="flex-1 disabled:opacity-100"
                        disabled={!isEditingCommission}
                      />
                    </div>
                    
                    {/* Auto-calculated Tenant Split % */}
                    <div className="flex items-baseline gap-4">
                      <Label className="text-sm w-40">Commission Split % - {account.tenant_name || 'Tenant'}</Label>
                      <span className="text-sm font-medium">
                        {(() => {
                          const linkedPercent = isEditingCommission 
                            ? (parseFloat(commissionForm.commission_split_percent_linked) || 0)
                            : (parseFloat(account.commission_split_percent_linked) || 0);
                          return linkedPercent > 0 ? `${(100 - linkedPercent).toFixed(2)}%` : '-';
                        })()}
                      </span>
                    </div>
                  </div>
                </>
              )}
              
              <div className="flex items-center gap-2">
                <Label className="text-sm w-48">Withdrawals Fee (ShipIn)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={isEditingCommission ? commissionForm.withdrawals_fee_shipin : (account.withdrawals_fee_shipin || '')}
                  onChange={(e) => setCommissionForm({...commissionForm, withdrawals_fee_shipin: e.target.value})}
                  placeholder="e.g., 250.00"
                  className="flex-1 disabled:opacity-100"
                  disabled={!isEditingCommission}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm w-48">Withdrawals Fee (Sale)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={isEditingCommission ? commissionForm.withdrawals_fee_sale : (account.withdrawals_fee_sale || '')}
                  onChange={(e) => setCommissionForm({...commissionForm, withdrawals_fee_sale: e.target.value})}
                  placeholder="e.g., 500.00"
                  className="flex-1 disabled:opacity-100"
                  disabled={!isEditingCommission}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Label className="text-sm w-48">Expense Markup % (if any)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={isEditingCommission ? commissionForm.expense_markup_percent : (account.expense_markup_percent || '')}
                  onChange={(e) => setCommissionForm({...commissionForm, expense_markup_percent: e.target.value})}
                  placeholder="e.g., 10.00"
                  className="flex-1 disabled:opacity-100"
                  disabled={!isEditingCommission}
                />
              </div>
            </div>
            {isEditingCommission && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setCommissionForm({ commission_currency: account.commission_currency || 'USD', commission_rate_percent: account.commission_rate_percent || '', minimum_commission_amount: account.minimum_commission_amount || '', commission_split: account.commission_split || false, commission_split_with: account.commission_split_with || '', commission_split_with_entity_id: account.commission_split_with_entity_id || '', commission_split_percent_entity: account.commission_split_percent_entity || '', commission_split_percent_linked: account.commission_split_percent_linked || '', withdrawals_fee_shipin: account.withdrawals_fee_shipin || '', withdrawals_fee_sale: account.withdrawals_fee_sale || '', expense_markup_percent: account.expense_markup_percent || '' }); setIsEditingCommission(false); setCommissionOpen(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSaveCommission} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
          </CollapsibleContent>
        </Card>
        </Collapsible>

        {/* Other Financial Notes - Collapsible */}
        <Collapsible open={notesOpen} onOpenChange={setNotesOpen}>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CollapsibleTrigger className="flex items-center gap-2 hover:opacity-80 cursor-pointer">
              <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform [[data-state=closed]_&]:-rotate-90" />
              <CardTitle className="text-base">Other Financial Notes</CardTitle>
            </CollapsibleTrigger>
            {canEditAccount && !isEditingNotes && (
              <Button variant="ghost" size="icon" onClick={() => { setIsEditingNotes(true); setNotesOpen(true); }}>
                <Pencil className="w-4 h-4" />
              </Button>
            )}
          </CardHeader>
          <CollapsibleContent>
          <CardContent>
            <Textarea
              value={isEditingNotes ? financialNotes : (account.other_financial_notes || '')}
              onChange={(e) => setFinancialNotes(e.target.value)}
              placeholder="Additional financial notes..."
              rows={8}
              disabled={!isEditingNotes}
              className="resize-none disabled:opacity-100"
            />
            {isEditingNotes && (
              <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => { setFinancialNotes(account.other_financial_notes || ''); setIsEditingNotes(false); setNotesOpen(false); }} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSaveNotes} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
              </div>
            )}
          </CardContent>
          </CollapsibleContent>
        </Card>
        </Collapsible>
      </div>

      <InvoicePdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        type={previewType}
        invoiceId={previewInvoiceId}
        statementData={previewStmtData}
      />
    </div>
  );
};

export default FinancialsTab;
